import React, { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  Plus, X, Save, Pencil, Users as UsersIcon, Loader2,
  Armchair, Receipt, Ban, Banknote, Landmark, CreditCard, Clock, ShoppingBag,
  Trash2, RefreshCw,
} from "lucide-react";
import ProductPicker from "../../components/company/ProductPicker";
import CustomerPicker from "../../components/company/CustomerPicker";
import InvoiceActions from "../../components/company/InvoiceActions";
import ItemSwapModal from "../../components/company/ItemSwapModal";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

const PAYMENT_METHODS = [
  { value: "efectivo", label: "Efectivo", icon: Banknote },
  { value: "transferencia", label: "Transferencia", icon: Landmark },
  { value: "tarjeta", label: "Tarjeta", icon: CreditCard },
];

function getCompanyIdFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.companyId ?? u?.company?.id ?? null;
  } catch { return null; }
}

function timeAgo(dateStr) {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  return `hace ${hours}h ${mins % 60}m`;
}

function customerLabelOf(order) {
  if (order?.customer) return `${order.customer.name}${order.customer.phone ? ` (${order.customer.phone})` : ""}`;
  if (order?.customerName) return `${order.customerName} (sin cuenta)`;
  return "—";
}

const NEW_TABLE_EMPTY = { label: "", capacity: 4, sedeId: "" };

// Primera posición del plano que no quede encima de otra mesa (porcentajes del plano).
function findFreeSpot(tables) {
  const taken = tables.map((t) => ({ x: Number(t.posX), y: Number(t.posY) }));
  for (let y = 15; y <= 85; y += 20) {
    for (let x = 15; x <= 85; x += 20) {
      if (!taken.some((t) => Math.abs(t.x - x) < 16 && Math.abs(t.y - y) < 22)) return { x, y };
    }
  }
  return { x: 50, y: 50 };
}

export default function CompanyTables() {
  const companyId = getCompanyIdFromStorage();

  const [tables, setTables] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [sedeFilter, setSedeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [ticket, setTicket] = useState(null); // { table, order }
  const [ticketLoading, setTicketLoading] = useState(false);
  const [pickerMode, setPickerMode] = useState(null); // null | 'open' | 'round'
  const [payMode, setPayMode] = useState(false);
  const [closedOrder, setClosedOrder] = useState(null); // cuenta recién cobrada — para mostrar acciones de factura
  const [busy, setBusy] = useState(false);

  const [newTableOpen, setNewTableOpen] = useState(false);
  const [newTableForm, setNewTableForm] = useState(NEW_TABLE_EMPTY);
  const [staff, setStaff] = useState([]);
  const [openCustomer, setOpenCustomer] = useState({});
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [me, setMe] = useState(null); // { sedeId, sedeName, unrestricted }
  const [editTable, setEditTable] = useState(null); // mesa en edición (nombre, capacidad, sede)
  const [editTableForm, setEditTableForm] = useState({ label: "", capacity: 4, sedeId: "" });
  const [swapItem, setSwapItem] = useState(null); // ítem de la cuenta que se está cambiando
  const [itemBusy, setItemBusy] = useState(false);

  // Venta para llevar/mostrador, sin mesa — para cuando todas están ocupadas pero sigue
  // llegando gente a comprar. Queda "en preparación" hasta que el mesero la cierra y
  // factura (ver pendingSales / el drawer de venta rápida más abajo).
  const [quickSaleOpen, setQuickSaleOpen] = useState(false);
  const [quickSaleCustomer, setQuickSaleCustomer] = useState({});
  const [quickSaleSedeId, setQuickSaleSedeId] = useState("");
  const [quickSaleBusy, setQuickSaleBusy] = useState(false);

  const [pendingSales, setPendingSales] = useState([]);
  const [qsDrawerId, setQsDrawerId] = useState(null);
  const [qsOrder, setQsOrder] = useState(null);
  const [qsLoading, setQsLoading] = useState(false);
  const [qsPayMode, setQsPayMode] = useState(false);
  const [qsClosedOrder, setQsClosedOrder] = useState(null);
  const [qsBusy, setQsBusy] = useState(false);
  const [qsSwapItem, setQsSwapItem] = useState(null); // ítem de la venta rápida que se está cambiando
  const [qsItemBusy, setQsItemBusy] = useState(false);
  const [qsEditingCustomer, setQsEditingCustomer] = useState(false);

  const containerRef = useRef(null);
  const dragRef = useRef(null); // { tableId }

  // Un empleado solo ve la sede que tiene asignada; el dueño/admin elige con el filtro.
  const effectiveSede = me && !me.unrestricted ? me.sedeId : sedeFilter;
  const noSedeAssigned = !!me && !me.unrestricted && !me.sedeId;

  const loadTables = useCallback(async () => {
    if (!me || noSedeAssigned) { setLoading(false); return; }
    try {
      const res = await API.get("/tables", { params: effectiveSede ? { sedeId: effectiveSede } : {} });
      setTables(res.data?.data?.tables ?? []);
    } catch (err) { toast.error(err.response?.data?.message || "Error cargando mesas"); }
    finally { setLoading(false); }
  }, [me, noSedeAssigned, effectiveSede]);

  const loadPendingSales = useCallback(async () => {
    if (!me || noSedeAssigned) return;
    try {
      const res = await API.get("/tables/quick-sale", { params: effectiveSede ? { sedeId: effectiveSede } : {} });
      setPendingSales(res.data?.data?.orders ?? []);
    } catch { /* silencioso — no es crítico como la carga inicial de mesas */ }
  }, [me, noSedeAssigned, effectiveSede]);

  useEffect(() => { loadTables(); }, [loadTables]);
  useEffect(() => { loadPendingSales(); }, [loadPendingSales]);

  useEffect(() => {
    if (!companyId) return;
    API.get(`/sedes/company/${companyId}`)
      .then((res) => setSedes(res.data?.data?.sedes ?? res.data?.data ?? []))
      .catch(() => {});
  }, [companyId]);

  // Lista de empleados de la empresa, para poder reasignar quién atiende una mesa.
  useEffect(() => {
    if (!companyId) return;
    API.get(`/companies/${companyId}/users`)
      .then((res) => setStaff(res.data?.data?.users ?? []))
      .catch(() => {});
  }, [companyId]);

  // Crear/editar/mover/eliminar mesas es gestión del salón — solo el dueño o un
  // rol con el permiso 'mesas_admin' puede ver esos controles (un mesero normal
  // solo opera las mesas: abrir, agregar ítems, cobrar).
  useEffect(() => {
    API.get("/auth/me")
      .then((res) => {
        const user = res.data?.data?.user ?? res.data?.user;
        if (!user) return;
        const isOwner = !!(user.company?.ownerId && user.id === user.company.ownerId);
        const allowed = isOwner || !user.companyRoleId || (user.companyRole?.modules ?? []).includes("mesas_admin");
        setCanManage(allowed);
        setMe({
          sedeId: user.sedeId ?? null,
          sedeName: user.sede?.name ?? null,
          unrestricted: isOwner || user.role === "admin",
        });
      })
      .catch(() => setLoading(false));
  }, []);

  // Socket en vivo — otro mesero ocupa/libera una mesa o abre/cierra una venta rápida
  // desde otro dispositivo.
  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;
    const socket = io(SOCKET_URL, { transports: ["websocket"], auth: { token } });
    socket.on("table:update", () => {
      loadTables();
      loadPendingSales();
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTables, loadPendingSales]);

  const openTicket = async (tableId) => {
    setSelectedId(tableId);
    setPickerMode(null);
    setPayMode(false);
    setClosedOrder(null);
    setTicketLoading(true);
    try {
      const res = await API.get(`/tables/${tableId}/ticket`);
      setTicket(res.data?.data ?? null);
    } catch { toast.error("Error cargando la mesa"); }
    finally { setTicketLoading(false); }
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setTicket(null);
    setPickerMode(null);
    setPayMode(false);
    setClosedOrder(null);
    setOpenCustomer({});
    setEditingCustomer(false);
  };

  const refreshTicket = async (tableId) => {
    const res = await API.get(`/tables/${tableId}/ticket`);
    setTicket(res.data?.data ?? null);
  };

  const handleOpenTable = async (items) => {
    if (!openCustomer.customerId && !openCustomer.customerName) {
      toast.warn("Debes asignar un cliente a la mesa");
      return;
    }
    setBusy(true);
    try {
      await API.post(`/tables/${selectedId}/open`, {
        items,
        customerId: openCustomer.customerId,
        customerName: openCustomer.customerName,
      });
      toast.success("Mesa abierta");
      setPickerMode(null);
      setOpenCustomer({});
      await Promise.all([refreshTicket(selectedId), loadTables()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al abrir la mesa"); }
    finally { setBusy(false); }
  };

  const handleAssignCustomer = async (customer) => {
    try {
      await API.post(`/tables/${selectedId}/customer`, {
        customerId: customer.customerId,
        customerName: customer.customerName,
      });
      toast.success("Cliente actualizado");
      setEditingCustomer(false);
      await Promise.all([refreshTicket(selectedId), loadTables()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al asignar el cliente"); }
  };

  const handleAssignQsCustomer = async (customer) => {
    try {
      await API.post(`/tables/quick-sale/${qsDrawerId}/customer`, {
        customerId: customer.customerId,
        customerName: customer.customerName,
      });
      toast.success("Cliente actualizado");
      setQsEditingCustomer(false);
      await Promise.all([refreshQsOrder(qsDrawerId), loadPendingSales()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al asignar el cliente"); }
  };

  const handleAddRound = async (items) => {
    setBusy(true);
    try {
      await API.post(`/tables/${selectedId}/items`, { items });
      toast.success("Ronda agregada");
      setPickerMode(null);
      await Promise.all([refreshTicket(selectedId), loadTables()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al agregar la ronda"); }
    finally { setBusy(false); }
  };

  const handleCheckout = async (paymentMethod) => {
    setBusy(true);
    try {
      const res = await API.post(`/tables/${selectedId}/checkout`, { paymentMethod });
      const order = res.data?.data?.order;
      toast.success(`Cuenta cobrada${order?.total ? ` · $${Number(order.total).toLocaleString("es-CO")}` : ""}`);
      setPayMode(false);
      setClosedOrder(order ?? null);
      await loadTables();
    } catch (err) { toast.error(err.response?.data?.message || "Error al cobrar"); }
    finally { setBusy(false); }
  };

  const handleReassign = async (userId) => {
    if (!userId) return;
    try {
      await API.post(`/tables/${selectedId}/reassign`, { userId });
      toast.success("Mesa reasignada");
      await Promise.all([refreshTicket(selectedId), loadTables()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al reasignar"); }
  };

  const handleVoidItem = async (item) => {
    if (!window.confirm(`¿Quitar "${item.title}" de la cuenta? Se repone el inventario.`)) return;
    setItemBusy(true);
    try {
      await API.delete(`/tables/${selectedId}/items/${item.id}`);
      toast.success("Ítem anulado");
      await Promise.all([refreshTicket(selectedId), loadTables()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al anular el ítem"); }
    finally { setItemBusy(false); }
  };

  const handleSwapItem = async (productId, quantity) => {
    setItemBusy(true);
    try {
      await API.patch(`/tables/${selectedId}/items/${swapItem.id}`, { productId, quantity });
      toast.success("Ítem actualizado");
      setSwapItem(null);
      await Promise.all([refreshTicket(selectedId), loadTables()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al actualizar el ítem"); }
    finally { setItemBusy(false); }
  };

  const handleVoid = async () => {
    if (!window.confirm("¿Anular esta mesa? Se repondrá el inventario descontado.")) return;
    setBusy(true);
    try {
      await API.post(`/tables/${selectedId}/void`);
      toast.success("Mesa anulada");
      closeDrawer();
      await loadTables();
    } catch (err) { toast.error(err.response?.data?.message || "Error al anular"); }
    finally { setBusy(false); }
  };

  const handleCreateTable = async () => {
    if (!newTableForm.label.trim()) { toast.warn("El nombre de la mesa es requerido"); return; }
    if (!newTableForm.sedeId) { toast.warn("Selecciona la sede de la mesa"); return; }
    const spot = findFreeSpot(tables);
    try {
      await API.post("/tables", {
        label: newTableForm.label.trim(),
        capacity: Number(newTableForm.capacity) || 4,
        sedeId: newTableForm.sedeId,
        posX: spot.x,
        posY: spot.y,
      });
      toast.success("Mesa creada");
      setNewTableOpen(false);
      setNewTableForm(NEW_TABLE_EMPTY);
      await loadTables();
    } catch (err) { toast.error(err.response?.data?.message || "Error al crear la mesa"); }
  };

  const openEditTable = (table) => {
    setEditTable(table);
    setEditTableForm({ label: table.label, capacity: table.capacity, sedeId: table.sedeId ?? "" });
  };

  const handleSaveTable = async () => {
    if (!editTableForm.label.trim()) { toast.warn("El nombre de la mesa es requerido"); return; }
    const data = { label: editTableForm.label.trim(), capacity: Number(editTableForm.capacity) || 4 };
    if (me?.unrestricted && editTableForm.sedeId) data.sedeId = editTableForm.sedeId;
    try {
      await API.patch(`/tables/${editTable.id}`, data);
      toast.success("Mesa actualizada");
      setEditTable(null);
      await loadTables();
    } catch (err) { toast.error(err.response?.data?.message || "Error al guardar la mesa"); }
  };

  const handleAssignTableSede = async (table, sedeId) => {
    if (!sedeId) return;
    try {
      await API.patch(`/tables/${table.id}`, { sedeId });
      toast.success(`Mesa "${table.label}" asignada`);
      await loadTables();
    } catch (err) { toast.error(err.response?.data?.message || "Error al asignar la sede"); }
  };

  const handleDeleteTable = async (table) => {
    if (table.status === "ocupada") return;
    if (!window.confirm(`¿Eliminar "${table.label}"?`)) return;
    try {
      await API.delete(`/tables/${table.id}`);
      await loadTables();
    } catch (err) { toast.error(err.response?.data?.message || "Error al eliminar"); }
  };

  // ── Venta rápida (para llevar, sin mesa) ────────────────────────────────────
  const openQuickSale = () => {
    setQuickSaleCustomer({});
    setQuickSaleSedeId(effectiveSede || (sedes.length === 1 ? sedes[0].id : ""));
    setQuickSaleOpen(true);
  };

  const closeQuickSale = () => {
    setQuickSaleOpen(false);
    setQuickSaleCustomer({});
  };

  const handleQuickSaleItems = async (items) => {
    if (!quickSaleCustomer.customerId && !quickSaleCustomer.customerName) {
      toast.warn("Debes asignar un cliente");
      return;
    }
    if (!quickSaleSedeId) { toast.warn("Selecciona la sede de la venta"); return; }
    setQuickSaleBusy(true);
    try {
      await API.post("/tables/quick-sale", {
        items,
        customerId: quickSaleCustomer.customerId,
        customerName: quickSaleCustomer.customerName,
        sedeId: quickSaleSedeId || undefined,
      });
      toast.success("Venta en preparación");
      closeQuickSale();
      await loadPendingSales();
    } catch (err) { toast.error(err.response?.data?.message || "Error al registrar la venta"); }
    finally { setQuickSaleBusy(false); }
  };

  const openQsDrawer = async (orderId) => {
    setQsDrawerId(orderId);
    setQsPayMode(false);
    setQsClosedOrder(null);
    setQsEditingCustomer(false);
    setQsLoading(true);
    try {
      const res = await API.get(`/tables/quick-sale/${orderId}`);
      setQsOrder(res.data?.data?.order ?? null);
    } catch { toast.error("Error cargando la venta"); }
    finally { setQsLoading(false); }
  };

  const closeQsDrawer = () => {
    setQsDrawerId(null);
    setQsOrder(null);
    setQsPayMode(false);
    setQsClosedOrder(null);
    setQsEditingCustomer(false);
  };

  const refreshQsOrder = async (orderId) => {
    const res = await API.get(`/tables/quick-sale/${orderId}`);
    setQsOrder(res.data?.data?.order ?? null);
  };

  const handleVoidQsItem = async (item) => {
    if (!window.confirm(`¿Quitar "${item.title}" de la venta? Se repone el inventario.`)) return;
    setQsItemBusy(true);
    try {
      await API.delete(`/tables/quick-sale/${qsDrawerId}/items/${item.id}`);
      toast.success("Ítem anulado");
      await Promise.all([refreshQsOrder(qsDrawerId), loadPendingSales()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al anular el ítem"); }
    finally { setQsItemBusy(false); }
  };

  const handleSwapQsItem = async (productId, quantity) => {
    setQsItemBusy(true);
    try {
      await API.patch(`/tables/quick-sale/${qsDrawerId}/items/${qsSwapItem.id}`, { productId, quantity });
      toast.success("Ítem actualizado");
      setQsSwapItem(null);
      await Promise.all([refreshQsOrder(qsDrawerId), loadPendingSales()]);
    } catch (err) { toast.error(err.response?.data?.message || "Error al actualizar el ítem"); }
    finally { setQsItemBusy(false); }
  };

  const handleQsClose = async (paymentMethod) => {
    setQsBusy(true);
    try {
      const res = await API.post(`/tables/quick-sale/${qsDrawerId}/close`, { paymentMethod });
      const order = res.data?.data?.order;
      toast.success(`Venta facturada${order?.total ? ` · $${Number(order.total).toLocaleString("es-CO")}` : ""}`);
      setQsPayMode(false);
      setQsClosedOrder(order ?? null);
      await loadPendingSales();
    } catch (err) { toast.error(err.response?.data?.message || "Error al facturar"); }
    finally { setQsBusy(false); }
  };

  const handleQsVoid = async () => {
    if (!window.confirm("¿Anular esta venta? Se repondrá el inventario descontado.")) return;
    setQsBusy(true);
    try {
      await API.post(`/tables/quick-sale/${qsDrawerId}/void`);
      toast.success("Venta anulada");
      closeQsDrawer();
      await loadPendingSales();
    } catch (err) { toast.error(err.response?.data?.message || "Error al anular"); }
    finally { setQsBusy(false); }
  };

  // ── Drag (modo edición) ───────────────────────────────────────────────────
  const clamp = (n) => Math.max(2, Math.min(98, n));

  const handlePointerDown = (e, table) => {
    if (!editMode) return;
    e.preventDefault();
    dragRef.current = { tableId: table.id, posX: null, posY: null };
    e.target.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const posX = clamp(((e.clientX - rect.left) / rect.width) * 100);
    const posY = clamp(((e.clientY - rect.top) / rect.height) * 100);
    drag.posX = posX;
    drag.posY = posY;
    setTables((prev) => prev.map((t) => (t.id === drag.tableId ? { ...t, posX, posY } : t)));
  };

  const handlePointerUp = async () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.posX === null) return;
    try { await API.patch(`/tables/${drag.tableId}/position`, { posX: drag.posX, posY: drag.posY }); }
    catch { toast.error("Error al guardar la posición"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 pt-4 pb-16">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <Armchair className="text-supernova-600" size={28} />
          Mesas
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {me?.unrestricted && sedes.length > 1 && (
            <select
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white"
              value={sedeFilter}
              onChange={(e) => setSedeFilter(e.target.value)}
            >
              <option value="">Todas las sedes</option>
              {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {!editMode && (
            <button
              onClick={openQuickSale}
              className="flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold transition"
            >
              <ShoppingBag size={15} /> Venta rápida
            </button>
          )}
          {editMode && canManage && (
            <button
              onClick={() => {
                setNewTableForm({ ...NEW_TABLE_EMPTY, sedeId: effectiveSede || (sedes.length === 1 ? sedes[0].id : "") });
                setNewTableOpen(true);
              }}
              className="flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-semibold transition"
            >
              <Plus size={15} /> Nueva mesa
            </button>
          )}
          {canManage && (
            <button
              onClick={() => setEditMode((v) => !v)}
              className={`flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                editMode ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300"
              }`}
            >
              <Pencil size={15} /> {editMode ? "Terminar edición" : "Editar mapa"}
            </button>
          )}
        </div>
      </div>

      {editMode && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4">
          Arrastra las mesas para ubicarlas. Solo se pueden eliminar mesas libres.
        </p>
      )}

      {/* Ventas para llevar en preparación */}
      {pendingSales.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            Para llevar — en preparación ({pendingSales.length})
          </p>
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1">
            {pendingSales.map((o) => (
              <button
                key={o.id}
                onClick={() => openQsDrawer(o.id)}
                className="shrink-0 w-44 text-left rounded-2xl border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 transition p-3"
              >
                <p className="font-bold text-sm text-gray-800 truncate">{customerLabelOf(o)}</p>
                <p className="text-xs text-gray-500 truncate">{o.orderNumber}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-amber-700 font-semibold flex items-center gap-1">
                    <Clock size={10} /> {timeAgo(o.createdAt)}
                  </span>
                  <span className="text-xs font-black text-gray-900">${Number(o.total ?? 0).toLocaleString("es-CO")}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {noSedeAssigned && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
          Tu usuario no tiene una sede asignada, por eso no puedes ver ni atender mesas. Pídele al dueño que te asigne una sede.
        </p>
      )}

      {/* Canvas — en pantallas angostas se desplaza dentro del recuadro, no la página */}
      {!noSedeAssigned && (
      <div className="w-full overflow-x-auto border border-gray-200 rounded-2xl">
      <div
        ref={containerRef}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="relative bg-gray-50"
        style={{
          width: "100%",
          minWidth: 640,
          minHeight: "65vh",
          backgroundImage: "radial-gradient(circle, #e5e7eb 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      >
        {tables.length === 0 && !editMode && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <Armchair size={40} className="mb-2 opacity-30" />
            <p className="text-sm">No hay mesas todavía</p>
            <p className="text-xs mt-1">Activa "Editar mapa" para crear la primera</p>
          </div>
        )}

        {tables.map((t) => {
          const isOcupada = t.status === "ocupada";
          return (
            <div
              key={t.id}
              onPointerDown={(e) => handlePointerDown(e, t)}
              onClick={() => !editMode && openTicket(t.id)}
              style={{
                position: "absolute",
                left: `${t.posX}%`,
                top: `${t.posY}%`,
                transform: "translate(-50%, -50%)",
                touchAction: editMode ? "none" : "auto",
              }}
              className={`w-24 h-24 rounded-2xl border-2 flex flex-col items-center justify-center gap-0.5 shadow-sm select-none transition ${
                editMode ? "cursor-move" : "cursor-pointer hover:shadow-md"
              } ${
                isOcupada
                  ? "border-supernova-400 bg-supernova-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              {editMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteTable(t); }}
                  disabled={isOcupada}
                  title={isOcupada ? "No se puede eliminar: tiene cuenta abierta" : "Eliminar mesa"}
                  className={`absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-white ${
                    isOcupada ? "bg-gray-300 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  <X size={11} />
                </button>
              )}
              <p className="font-bold text-sm text-gray-800 text-center px-1 leading-tight">{t.label}</p>
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <UsersIcon size={10} /> {t.capacity}
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                isOcupada ? "bg-supernova-500 text-white" : "bg-gray-100 text-gray-500"
              }`}>
                {isOcupada ? "Ocupada" : "Libre"}
              </span>
              {isOcupada && t.attendedBy && (
                <span className="text-[9px] text-supernova-700 font-semibold truncate max-w-[88px] px-1">
                  {t.attendedBy.name}
                </span>
              )}
              {editMode && (
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); openEditTable(t); }}
                  title="Editar nombre, capacidad o sede"
                  className="absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center bg-gray-900 text-white hover:bg-gray-700"
                >
                  <Pencil size={10} />
                </button>
              )}
              {!t.sedeId && editMode && (
                <select
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleAssignTableSede(t, e.target.value)}
                  defaultValue=""
                  className="text-[9px] border border-red-300 rounded px-0.5 py-0 bg-white max-w-[84px]"
                  title="Asignar sede a esta mesa"
                >
                  <option value="">Asignar sede…</option>
                  {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              {!t.sedeId && !editMode && (
                <span className="text-[9px] text-red-600 font-semibold">Sin sede</span>
              )}
            </div>
          );
        })}
      </div>
      </div>
      )}

      {/* Modal editar mesa */}
      {editTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditTable(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-800 mb-4">Editar mesa</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                <input
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  value={editTableForm.label}
                  onChange={(e) => setEditTableForm({ ...editTableForm, label: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Capacidad (personas)</label>
                <input
                  type="number" min={1}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  value={editTableForm.capacity}
                  onChange={(e) => setEditTableForm({ ...editTableForm, capacity: e.target.value })}
                />
              </div>
              {me?.unrestricted && sedes.length > 1 && (
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Sede *</label>
                  <select
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                    value={editTableForm.sedeId}
                    onChange={(e) => setEditTableForm({ ...editTableForm, sedeId: e.target.value })}
                  >
                    <option value="">Selecciona una sede</option>
                    {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditTable(null)} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold">
                Cancelar
              </button>
              <button onClick={handleSaveTable} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-semibold">
                <Save size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva mesa */}
      {newTableOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setNewTableOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-800 mb-4">Nueva mesa</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                <input
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  value={newTableForm.label}
                  onChange={(e) => setNewTableForm({ ...newTableForm, label: e.target.value })}
                  placeholder="Mesa 1, Barra 2..."
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Capacidad</label>
                <input
                  type="number" min={1}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  value={newTableForm.capacity}
                  onChange={(e) => setNewTableForm({ ...newTableForm, capacity: e.target.value })}
                />
              </div>
              {me?.unrestricted && sedes.length > 1 && (
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Sede *</label>
                  <select
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                    value={newTableForm.sedeId}
                    onChange={(e) => setNewTableForm({ ...newTableForm, sedeId: e.target.value })}
                  >
                    <option value="">Selecciona una sede</option>
                    {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setNewTableOpen(false)} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold">
                Cancelar
              </button>
              <button onClick={handleCreateTable} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-semibold">
                <Save size={14} /> Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal venta rápida (sin mesa) — crea la venta "en preparación"; el cobro pasa
          luego, desde la tarjeta de "Para llevar" cuando ya esté lista. */}
      {quickSaleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeQuickSale}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <ShoppingBag size={17} className="text-supernova-600" /> Venta rápida
              </h3>
              <button onClick={closeQuickSale} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {me?.unrestricted && sedes.length > 1 && (
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1.5 block">Sede *</label>
                  <select
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                    value={quickSaleSedeId}
                    onChange={(e) => setQuickSaleSedeId(e.target.value)}
                  >
                    <option value="">Selecciona una sede</option>
                    {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Cliente *</label>
                <CustomerPicker value={quickSaleCustomer} onChange={setQuickSaleCustomer} />
              </div>
              <ProductPicker
                companyId={companyId}
                submitLabel={quickSaleBusy ? "Registrando…" : "Enviar a preparar"}
                onConfirm={handleQuickSaleItems}
                onCancel={closeQuickSale}
              />
            </div>
          </div>
        </div>
      )}

      {/* Drawer de venta rápida (para llevar) */}
      {qsDrawerId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeQsDrawer}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <ShoppingBag size={16} className="text-supernova-600" /> {qsOrder?.orderNumber ?? "Venta para llevar"}
              </h3>
              <button onClick={closeQsDrawer} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {qsLoading ? (
                <div className="flex items-center justify-center py-10"><Loader2 size={24} className="animate-spin text-supernova-500" /></div>
              ) : qsClosedOrder ? (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <Receipt size={32} className="mx-auto mb-2 text-supernova-500" />
                    <p className="text-sm font-bold text-gray-800">Venta facturada</p>
                    <p className="text-2xl font-black text-gray-900 mt-1">${Number(qsClosedOrder.total ?? 0).toLocaleString("es-CO")}</p>
                  </div>
                  <InvoiceActions orderId={qsClosedOrder.id} sedeId={qsClosedOrder.sedeId} />
                </div>
              ) : qsPayMode ? (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-1">Cobrar venta</p>
                  <p className="text-2xl font-black text-gray-900 mb-4">
                    ${Number(qsOrder?.total ?? 0).toLocaleString("es-CO")}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">Método de pago</p>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {PAYMENT_METHODS.map((pm) => (
                      <button
                        key={pm.value}
                        disabled={qsBusy}
                        onClick={() => handleQsClose(pm.value)}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-gray-200 hover:border-supernova-400 hover:bg-supernova-50 transition disabled:opacity-50"
                      >
                        <pm.icon size={18} className="text-gray-600" />
                        <span className="text-xs font-semibold text-gray-700">{pm.label}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setQsPayMode(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancelar</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs text-gray-400">Cliente</span>
                      {!qsEditingCustomer && (
                        <button onClick={() => setQsEditingCustomer(true)} className="text-supernova-600 hover:text-supernova-700">
                          <Pencil size={12} />
                        </button>
                      )}
                    </div>
                    {qsEditingCustomer ? (
                      <CustomerPicker
                        value={{
                          customerId: qsOrder?.customer?.id,
                          customerLabel: qsOrder?.customer?.name,
                          customerName: qsOrder?.customerName,
                        }}
                        onChange={handleAssignQsCustomer}
                      />
                    ) : (
                      <p className="text-sm text-gray-700">{customerLabelOf(qsOrder)}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Pedido por</span>
                    <p className="text-sm text-gray-700">{qsOrder?.user?.name ?? "—"}</p>
                  </div>
                  <div className="space-y-1.5">
                    {(qsOrder?.items ?? []).map((it) => (
                      <div key={it.id} className="flex items-center justify-between text-sm gap-2">
                        <span className="text-gray-700 flex-1 min-w-0 truncate">{it.quantity}x {it.title}</span>
                        <span className="text-gray-500 shrink-0">${(Number(it.price) * it.quantity).toLocaleString("es-CO")}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setQsSwapItem(it)}
                            disabled={qsItemBusy}
                            title="Cambiar producto o cantidad"
                            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-supernova-600 hover:bg-supernova-50 transition disabled:opacity-40"
                          >
                            <RefreshCw size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVoidQsItem(it)}
                            disabled={qsItemBusy}
                            title="Quitar de la venta"
                            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-40"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-sm font-bold text-gray-700">Total</span>
                    <span className="text-xl font-black text-gray-900">${Number(qsOrder?.total ?? 0).toLocaleString("es-CO")}</span>
                  </div>
                </div>
              )}
            </div>

            {!qsLoading && !qsPayMode && !qsClosedOrder && (
              <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
                <button onClick={() => setQsPayMode(true)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold">
                  <Receipt size={14} /> Cerrar y facturar
                </button>
                <button onClick={handleQsVoid} disabled={qsBusy} className="px-3 py-2.5 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50">
                  <Ban size={14} />
                </button>
              </div>
            )}
            {qsClosedOrder && (
              <div className="px-6 py-4 border-t border-gray-100">
                <button onClick={closeQsDrawer} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold">
                  Listo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Drawer de mesa */}
      {selectedId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeDrawer}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">{ticket?.table?.label ?? "Mesa"}</h3>
              <button onClick={closeDrawer} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {ticketLoading ? (
                <div className="flex items-center justify-center py-10"><Loader2 size={24} className="animate-spin text-supernova-500" /></div>
              ) : closedOrder ? (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <Receipt size={32} className="mx-auto mb-2 text-supernova-500" />
                    <p className="text-sm font-bold text-gray-800">Cuenta cobrada</p>
                    <p className="text-2xl font-black text-gray-900 mt-1">${Number(closedOrder.total ?? 0).toLocaleString("es-CO")}</p>
                  </div>
                  <InvoiceActions orderId={closedOrder.id} sedeId={closedOrder.sedeId} />
                </div>
              ) : pickerMode === "open" ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500 mb-1.5 block">Cliente de la mesa *</label>
                    <CustomerPicker value={openCustomer} onChange={setOpenCustomer} />
                  </div>
                  <ProductPicker companyId={companyId} submitLabel="Abrir mesa" onConfirm={handleOpenTable} onCancel={() => setPickerMode(null)} />
                </div>
              ) : pickerMode === "round" ? (
                <ProductPicker companyId={companyId} submitLabel="Agregar a la cuenta" onConfirm={handleAddRound} onCancel={() => setPickerMode(null)} />
              ) : ticket?.table?.status === "libre" ? (
                <div className="text-center py-10">
                  <Armchair size={36} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-gray-500 mb-4">Mesa libre</p>
                  <button
                    onClick={() => setPickerMode("open")}
                    className="px-5 py-2.5 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold"
                  >
                    Abrir mesa
                  </button>
                </div>
              ) : payMode ? (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-1">Cobrar cuenta</p>
                  <p className="text-2xl font-black text-gray-900 mb-4">
                    ${Number(ticket?.order?.total ?? 0).toLocaleString("es-CO")}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">Método de pago</p>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {PAYMENT_METHODS.map((pm) => (
                      <button
                        key={pm.value}
                        disabled={busy}
                        onClick={() => handleCheckout(pm.value)}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-gray-200 hover:border-supernova-400 hover:bg-supernova-50 transition disabled:opacity-50"
                      >
                        <pm.icon size={18} className="text-gray-600" />
                        <span className="text-xs font-semibold text-gray-700">{pm.label}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setPayMode(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancelar</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-400">Atendida por</span>
                    <select
                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs max-w-[160px]"
                      value={ticket?.order?.user?.id ?? ""}
                      onChange={(e) => handleReassign(e.target.value)}
                    >
                      {!staff.some((u) => u.id === ticket?.order?.user?.id) && (
                        <option value={ticket?.order?.user?.id ?? ""}>{ticket?.order?.user?.name ?? "—"}</option>
                      )}
                      {staff
                        .filter((u) => u.isOwner || u.sedeId === ticket?.table?.sedeId)
                        .map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs text-gray-400">Cliente</span>
                      {!editingCustomer && (
                        <button onClick={() => setEditingCustomer(true)} className="text-supernova-600 hover:text-supernova-700">
                          <Pencil size={12} />
                        </button>
                      )}
                    </div>
                    {editingCustomer ? (
                      <CustomerPicker
                        value={{
                          customerId: ticket?.order?.customer?.id,
                          customerLabel: ticket?.order?.customer?.name,
                          customerName: ticket?.order?.customerName,
                        }}
                        onChange={handleAssignCustomer}
                      />
                    ) : (
                      <p className="text-sm text-gray-700">{customerLabelOf(ticket?.order)}</p>
                    )}
                  </div>
                  {Object.entries(
                    (ticket?.order?.items ?? []).reduce((acc, it) => {
                      (acc[it.roundNumber] = acc[it.roundNumber] || []).push(it);
                      return acc;
                    }, {})
                  ).map(([round, items]) => (
                    <div key={round}>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        Ronda {round}
                        <span className="text-gray-400 font-normal flex items-center gap-0.5 normal-case">
                          <Clock size={10} /> {timeAgo(items[0].createdAt)}
                        </span>
                      </p>
                      <div className="space-y-1.5">
                        {items.map((it) => (
                          <div key={it.id} className="flex items-center justify-between text-sm gap-2 group">
                            <span className="text-gray-700 flex-1 min-w-0 truncate">{it.quantity}x {it.title}</span>
                            <span className="text-gray-500 shrink-0">${(Number(it.price) * it.quantity).toLocaleString("es-CO")}</span>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => setSwapItem(it)}
                                disabled={itemBusy}
                                title="Cambiar producto o cantidad"
                                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-supernova-600 hover:bg-supernova-50 transition disabled:opacity-40"
                              >
                                <RefreshCw size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleVoidItem(it)}
                                disabled={itemBusy}
                                title="Quitar de la cuenta"
                                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-40"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-sm font-bold text-gray-700">Total</span>
                    <span className="text-xl font-black text-gray-900">${Number(ticket?.order?.total ?? 0).toLocaleString("es-CO")}</span>
                  </div>
                </div>
              )}
            </div>

            {!pickerMode && !payMode && !closedOrder && ticket?.table?.status === "ocupada" && (
              <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
                <button onClick={() => setPickerMode("round")} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-semibold">
                  <Plus size={14} /> Agregar ítems
                </button>
                <button onClick={() => setPayMode(true)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold">
                  <Receipt size={14} /> Cobrar
                </button>
                <button onClick={handleVoid} disabled={busy} className="px-3 py-2.5 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50">
                  <Ban size={14} />
                </button>
              </div>
            )}
            {closedOrder && (
              <div className="px-6 py-4 border-t border-gray-100">
                <button onClick={closeDrawer} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold">
                  Listo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {swapItem && (
        <ItemSwapModal
          companyId={companyId}
          item={swapItem}
          saving={itemBusy}
          onSave={handleSwapItem}
          onCancel={() => setSwapItem(null)}
        />
      )}

      {qsSwapItem && (
        <ItemSwapModal
          companyId={companyId}
          item={qsSwapItem}
          saving={qsItemBusy}
          onSave={handleSwapQsItem}
          onCancel={() => setQsSwapItem(null)}
        />
      )}
    </div>
  );
}
